import React, { useState } from "react";
import Swal from "sweetalert2";
import ScrollToTop from "../ScrollToTop";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { isValidPhoneNumber } from "libphonenumber-js";

const RegisterYourInterestForm = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    repeatEmail: "",
    country: "",
    gender: "",
    qualification: "",
    course: "",
    address: "",
    nationality: "",
    employment: "",
    finance: "",
    joinIntake: "",
    phone: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const phoneInvalid = formData.phone && !isValidPhoneNumber(formData.phone);

  const fieldLabels = {
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    repeatEmail: "Repeat Email",
    country: "Country of Residence",
    gender: "Gender",
    qualification: "Highest Qualification",
    course: "Selected Course",
    address: "Address",
    nationality: "Nationality",
    employment: "Employment Status",
    finance: "Finance Plan",
    joinIntake: "Join March 2026 or June 2026 Intake",
    phone: "Phone (WhatsApp)",
  };

  const createFormattedData = () => {
    const formattedData = new FormData();

    formattedData.append("_subject", "New Registration Form Submission");
    formattedData.append("_captcha", "false");
    formattedData.append("_template", "table");

    Object.keys(formData).forEach((key) => {
      formattedData.append(fieldLabels[key], formData[key]);
    });

    return formattedData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.email !== formData.repeatEmail) {
      Swal.fire({
        icon: "error",
        title: "Emails do not match!",
        text: "Please make sure both email fields are the same.",
      });
      return;
    }

    if (!formData.phone || !isValidPhoneNumber(formData.phone)) {
      Swal.fire({
        icon: "error",
        title: "Invalid phone number",
        text: "Please enter a valid phone number (with country code).",
      });
      return;
    }

    setIsSubmitting(true);

    const formSubmitEndpoint = "https://formsubmit.co/sapphirediplomacyleadership@gmail.com"; // Your FormSubmit endpoint
    const data = createFormattedData();

    Swal.fire({
      title: "Submitting your form...",
      text: "Please wait a moment.",
      icon: "info",
      allowOutsideClick: false,
      showConfirmButton: false,
      timer: 10000,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await fetch(formSubmitEndpoint, {
        method: "POST",
        body: data,
      });

      setTimeout(() => {
        setIsSubmitting(false);

        if (response.ok) {
          Swal.fire({
            title: "Form Submitted!",
            text: "Your form has been submitted successfully.",
            icon: "success",
          });
          setFormData({
            firstName: "",
            lastName: "",
            email: "",
            repeatEmail: "",
            country: "",
            gender: "",
            qualification: "",
            course: "",
            address: "",
            nationality: "",
            employment: "",
            finance: "",
            joinIntake: "",
            phone: "",
          });
        } else {
          Swal.fire({
            title: "Submission Error",
            text: "There was an issue submitting your form. Please try again later.",
            icon: "error",
          });
        }
      }, 10000);
    } catch (error) {
      setIsSubmitting(false);
      Swal.fire({
        title: "Network Error",
        text: "There was a problem submitting your form. Please check your connection & try again.",
        icon: "error",
      });
    }
  };

  return (
    <div className="p-3">
      <ScrollToTop />
      <form
        onSubmit={handleSubmit}
        className="h-auto md:p-10 py-4 px-2 md:my-5 bg-blue-200 bg-opacity-70 backdrop-blur-sm border flex flex-col justify-center border-black rounded-md max-w-lg mx-auto"
      >
        <h2 className="text-2xl font-bold mb-6 text-center">
          Register Your Interest
        </h2>
        <div className="flex gap-4 mb-4">
          <input
            type="text"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            className="w-1/2 p-2 border rounded"
            required
          />
          <input
            type="text"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            className="w-1/2 p-2 border rounded"
            required
          />
        </div>
        <div className="flex gap-4 mb-4">
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            className="w-1/2 p-2 border rounded"
            required
          />
          <input
            type="email"
            name="repeatEmail"
            placeholder="Repeat Email"
            value={formData.repeatEmail}
            onChange={handleChange}
            className="w-1/2 p-2 border rounded"
            required
          />
        </div>
        <div className="flex gap-4 mb-4">
          <input
            type="text"
            name="country"
            placeholder="Country of Residence"
            value={formData.country}
            onChange={handleChange}
            className="w-1/2 p-2 border rounded"
            required
          />
          <select
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className="w-1/2 p-2 border rounded"
            required
          >
            <option value="" className="bg-gray-500 text-white" disabled>
              --Select Gender--
            </option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="space-y-1.5 mb-4">
          <div className="flex items-center justify-between">
            <label htmlFor="phone" className="text-sm font-medium">
              Phone <span className="text-muted-foreground">(WhatsApp)</span>
            </label>
            <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
              E.164 format
            </span>
          </div>
          <div
            className={[
              "w-full rounded-md border bg-sdbodywhite/50 px-3 py-2 shadow-sm ring-offset-background transition",
              "focus-within:outline-none focus-within:ring-2 focus-within:ring-[--ring] focus-within:ring-offset-2",
              phoneInvalid
                ? "border-red-500 focus-within:ring-red-500"
                : "border-border",
              "[&_.PhoneInput]:w-full [&_.PhoneInputCountry]:mr-2 [&_.PhoneInputCountryIcon]:rounded",
              "[&_.PhoneInputInput]:w-full [&_.PhoneInputInput]:bg-transparent [&_.PhoneInputInput]:outline-none",
              "[&_.PhoneInputInput]:border-0 [&_.PhoneInputInput]:p-0 [&_.PhoneInputInput]:h-6 [&_.PhoneInputInput]:text-sm",
            ].join(" ")}
          >
            <PhoneInput
              id="phone"
              defaultCountry="GH"
              international
              placeholder="Enter phone number"
              value={formData.phone}
              onChange={(val) =>
                setFormData((prev) => ({ ...prev, phone: val || "" }))
              }
              countryCallingCodeEditable={false}
              aria-invalid={phoneInvalid ? "true" : "false"}
              aria-describedby="phone-help"
            />
          </div>

          <p
            id="phone-help"
            className={`text-xs ${
              phoneInvalid ? "text-red-600" : "text-muted-foreground"
            }`}
          >
            Include your country code (e.g., +233). We may contact you via
            WhatsApp.
          </p>
        </div>

        <div className="mb-4">
          <select
            name="qualification"
            value={formData.qualification}
            onChange={handleChange}
            className="w-full p-2 border rounded"
            required
          >
            <option value="" className="bg-gray-500 text-white" disabled>
              --Highest Qualification--
            </option>
            <option value="High School">High School</option>
            <option value="Bachelor">Bachelor</option>
            <option value="Masters">Masters</option>
            <option value="PhD">PhD</option>
          </select>
        </div>

        <div className="mb-4">
          <select
            name="course"
            value={formData.course}
            onChange={handleChange}
            className="w-full p-2 border rounded"
            required
          >
            <option value="" className="bg-gray-500 text-white" disabled>
              --Select a Programme of Study--
            </option>
            <option value="Master of Arts in Global Business & Strategic Leadership">
              Master of Arts in Global Business & Strategic Leadership
            </option>
            {/* <option value="Advanced International Relations">
              Advanced International Relations
            </option>
            <option value="Emerging Women Leadership">
              Emerging Women Leadership
            </option>
            <option value="Executive Leadership & Development">
              Executive Leadership & Development
            </option>
            <option value="Emerging Entrepreneur">Emerging Entrepreneur</option>
            <option value="Emerging Journalist">Emerging Journalist</option>
            <option value="Emerging Young Politician">
              Emerging Young Politician
            </option>
            <option value="International Relations">
              International Relations
            </option>
            <option value="Emerging Young Trade Specialist">
              Emerging Young Trade Specialist
            </option>
            <option value="Certified Business Consultant">
              Certified Business Consultant
            </option>
            <option value="Certified Executive Career Coach">
              Certified Executive Career Coach
            </option>
            <option value="Certified Project Management Associate">
              Certified Project Management Associate
            </option>
            <option value="Certified Change Leader">
              Certified Change Leader
            </option>
            <option value="Certified Women Leadership & Gender Changemaker">
              Certified Women Leadership & Gender Changemaker
            </option>
            <option value="Certified Agile Thought Leader">
              Certified Agile Thought Leader
            </option> */}
          </select>
        </div>

        <div className="flex gap-4 mb-4">
          <input
            type="text"
            name="address"
            placeholder="Address"
            value={formData.address}
            onChange={handleChange}
            className="w-1/2 p-2 border rounded"
            required
          />
          <input
            type="text"
            name="nationality"
            placeholder="Nationality"
            value={formData.nationality}
            onChange={handleChange}
            className="w-1/2 p-2 border rounded"
            required
          />
        </div>

        <div className="bg-sdbodywhite mb-4 p-2 rounded-md">
          <div className="mb-4">
            <p className="mb-2 text-left font-medium">Are you in employment?</p>
            <div className="flex gap-4 font-light">
              <label>
                <input
                  type="radio"
                  name="employment"
                  value="Yes"
                  checked={formData.employment === "Yes"}
                  onChange={handleChange}
                  required
                />{" "}
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="employment"
                  value="No"
                  checked={formData.employment === "No"}
                  onChange={handleChange}
                  required
                />{" "}
                No
              </label>
            </div>
          </div>

          <div className="mb-4">
            <p className="mb-2 text-left font-medium">
              How do you plan to finance your studies?
            </p>
            <div className="flex gap-4 font-light">
              <label>
                <input
                  type="radio"
                  name="finance"
                  value="Self-Funded"
                  checked={formData.finance === "Self-Funded"}
                  onChange={handleChange}
                  required
                />{" "}
                Self-Funded
              </label>
              <label>
                <input
                  type="radio"
                  name="finance"
                  value="Other"
                  checked={formData.finance === "Other"}
                  onChange={handleChange}
                  required
                />{" "}
                Other
              </label>
            </div>
          </div>
          <div className="">
            <p className="mb-2 font-medium">
              Do you want to join the March 2027 or September 2027 intake?
            </p>
            <div className="flex gap-6 font-light">
              {["March 2027", "September 2027"].map((opt) => (
                <label key={opt} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="joinIntake"
                    value={opt}
                    checked={formData.joinIntake === opt}
                    onChange={handleChange}
                    required
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-black">{opt}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-black/70 border-2 border-red-600 text-red-500 p-2 rounded-lg">
          <p className="text-sm text-left ">
            Please check your Spam folder as emails from us may be diverted
            there by mistake.
            <br />
            To continue receiving our updates, please mark them as "Not Spam."
          </p>
        </div>
        <button
          type="submit"
          disabled={isSubmitting} 
          className={`px-6 py-2 mt-6 bg-blue-600 text-white rounded hover:bg-blue-700 ${
            isSubmitting ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default RegisterYourInterestForm;
